"use client";

import { useEffect } from "react";
import { create } from "zustand";
import { CHOSEN_COUNTRY_KEY, clearSavedRegion, detectRegionLocally } from "./detect-region";
import { DEFAULT_COUNTRY, EVENT_COUNTRIES } from "./event-countries";
import { fetchActiveCountries } from "./api";


const STORAGE_KEY = CHOSEN_COUNTRY_KEY;

// País de la persona, para filtrar eventos, ubicaciones y organizaciones.
// Por defecto el que indica el propio dispositivo (zona horaria / idioma, ver
// detect-region.ts: sin enviar la IP a nadie); si no tenemos eventos de ese
// país, o no se puede saber, Chile. Se guarda en localStorage (el sitio sigue
// siendo estático: el servidor nunca lo lee).
interface CountryState {
  country: string;
  // Países que ofrece el selector: los fijos + los que tienen eventos
  // vigentes (se cargan del backend al abrir el sitio).
  available: string[];
  // true una vez resuelto en el navegador. Antes de eso vale Chile, que es
  // también lo que renderiza el servidor.
  loaded: boolean;
  load: () => void;
  setCountry: (country: string) => void;
}

const FIXED: string[] = [...EVENT_COUNTRIES];

// Cualquier código de país válido: el guardado puede ser de un país que
// llegó por /events/countries (todavía no cargado al leerlo).
function readStored(): string | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && /^[A-Z]{2}$/.test(saved) ? saved : null;
  } catch {
    return null;
  }
}

function pick(available: string[], stored: string | null, detected: string): string {
  if (stored && available.includes(stored)) return stored;
  return available.includes(detected) ? detected : DEFAULT_COUNTRY;
}

// true si la persona eligió país a mano en esta visita: la lista del
// backend ya no le cambia la elección.
let chosenThisVisit = false;

export const useCountryStore = create<CountryState>((set, get) => ({
  country: DEFAULT_COUNTRY,
  available: FIXED,
  loaded: false,
  load: () => {
    if (get().loaded) return;
    const stored = readStored();
    const detected = detectRegionLocally();
    set({ country: pick(FIXED, stored, detected), loaded: true });
    fetchActiveCountries().then((codes) => {
      const available = [...new Set([...FIXED, ...codes])];
      set({
        available,
        ...(!chosenThisVisit && { country: pick(available, stored, detected) }),
      });
    });
  },
  setCountry: (country) => {
    if (!get().available.includes(country)) return;
    chosenThisVisit = true;
    try {
      localStorage.setItem(STORAGE_KEY, country);
    } catch {
      // Modo privado / storage bloqueado: la elección dura solo esta visita.
    }
    // Viva Cine vuelve a seguir el país global (se descarta el catálogo de
    // otro país que se haya elegido allá).
    clearSavedRegion();
    set({ country, loaded: true });
  },
}));

// País vigente + si ya se resolvió en el navegador. Lo carga la primera vez
// que algún componente lo usa.
export function useCountry(): { country: string; loaded: boolean } {
  const country = useCountryStore((s) => s.country);
  const loaded = useCountryStore((s) => s.loaded);
  const load = useCountryStore((s) => s.load);
  useEffect(() => {
    load();
  }, [load]);
  return { country, loaded };
}
